import { useState } from "react";
import { ArrowRight, Check, Clock, Languages } from "lucide-react";
import { cdnPath } from "@/utility/api";

const CardsForCourse = ({ courses, data, purchaseStatus }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getCardUrl = () => {
    if (purchaseStatus.hasPurchased) {
      return `/view-courses/${courses?.id}`;
    }
    if (purchaseStatus.isFirstTime) {
      return `/courses/${courses?.id}`;
    }
    return `/my-courses`;
  };

  return (
    <a
      href={getCardUrl()}
      className="relative overflow-hidden bg-[#0f1117] rounded-2xl shadow-lg border border-gray-800 hover:border-[#a78bfa]/50 transition-all duration-500 h-full flex flex-col cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image container */}
      <div className="relative w-full pt-[56.25%] overflow-hidden">
        <div
          className={`absolute inset-0 transition-transform duration-700 ${
            isHovered ? "scale-110" : "scale-100"
          }`}
          style={{
            backgroundImage: `url(${cdnPath}/${courses.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: "100%",
            height: "100%",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#0f1117]/40 to-transparent z-10"></div>
      </div>

      {/* Content */}
      <div className="relative z-20 p-6 flex-grow flex flex-col">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {courses.tags.map((tag) => (
            <span
              key={tag.name}
              className={`${tag.color} px-3 py-1 rounded-full text-xs font-semibold`}
            >
              {tag.name}
            </span>
          ))}
        </div>

        {/* Title */}
        <h2 className="text-xl md:text-3xl font-bold text-white mb-2 tracking-tight">
          {data?.title}
        </h2>

        {/* Description */}
        <p className="text-gray-300 mb-6">{courses?.description}</p>

        {/* Program details */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Clock className=" w-5 h-5  text-[#a78bfa] flex-shrink-0" />
            <span className="  text-gray-200">{courses?.duration}</span>
          </div>
          <div className="flex items-center gap-2">
            <Languages className="w-5 h-5 text-[#a78bfa]" />
            <span className="text-gray-200 text-sm">{data?.language}</span>
          </div>
        </div>

        {/* Features */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-white mb-3">
            What you'll learn:
          </h4>
          <ul className="space-y-2">
            {courses.features.slice(0, 3).map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-[#a78bfa] mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Spacer */}
        <div className="flex-grow"></div>

        {/* Price and CTA Button */}
        <div className="relative z-20 pt-4">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl font-bold text-white">
              ₹{data?.discounted_price}
            </span>
            {courses.price !== courses.discountedPrice && (
              <span className="text-lg text-gray-400 line-through">
                {courses.price}
              </span>
            )}
            <span className="bg-[#a78bfa]/20 text-[#a78bfa] text-xs px-2 py-1 rounded-full">
              Limited Time Offer
            </span>
          </div>

          <div onClick={(e) => e.stopPropagation()} className="block">
            <a
              href={getCardUrl()}
              className="w-full py-3 bg-[#a78bfa] hover:bg-[#9061f9] text-white rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-lg font-semibold group"
            >
              {!purchaseStatus?.hasPurchased ? (
                <>
                  {purchaseStatus?.isFirstTime ? (
                    <>{courses.cta}</>
                  ) : (
                    <span>Extend</span>
                  )}
                </>
              ) : (
                <span>View Course</span>
              )}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </a>
  );
};

export default CardsForCourse;
